import { useCallback, useEffect, useRef, useState } from "react"
import styled, { css, useTheme } from "styled-components"
import { Icon } from "~/ur-react-components"
import { useClickOutside, useTranslate } from "~/ur-react-hooks"
import { Input } from "../Input"
import type { AllowedOptionValueType, SelectOption } from "./Select"

interface LayoutProps {
  isOpen: boolean
  width?: string
}

const Layout = styled.div<LayoutProps>`
  position: relative;
  height: 48px;

  width: ${(props) => props.width};
  border-radius: 4px;
  box-sizing: border-box;
`

const Clear = styled.div`
  display: none;

  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  margin-top: 2px;
  margin-left: 1rem;

  &:hover {
    background-color: ${(props) => props.theme.colors.gray95};
  }
`

interface DisplayProps {
  isOpen: boolean
  width?: string
  hasError: boolean
  placeholderColor?: string
  isPlaceholderDisplayed: boolean
}
const Display = styled.div<DisplayProps>`
  display: flex;
  height: 48px;
  align-items: center;

  box-sizing: border-box;

  border-radius: 4px;
  color: ${(props) => props.theme.colors.gray0};
  width: ${(props) => props.width};

  border: 1px solid ${(props) => props.theme.colors.gray70};

  ${(props) =>
    props.isPlaceholderDisplayed &&
    props.placeholderColor &&
    css`
      color: ${props.placeholderColor};
    `}

  ${(props) =>
    props.isOpen &&
    css`
      border: 2px solid ${props.theme.colors.primary};

      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
      border-bottom: none;
    `}

  ${(props) =>
    props.hasError &&
    css`
      border: 2px solid ${props.theme.colors.error40};
    `}

  &:hover {
    ${Clear} {
      display: block;
    }
  }
`

interface DisplayInputProps {
  placeholderColor?: string
  hasError?: boolean
}

const DisplayInput = styled(Input)<DisplayInputProps>`
  width: 100%;

  input {
    background: none !important;
    outline: none !important;
    border: none !important;
  }
`

const ChevronWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  padding: 1rem;
  z-index: 4000;
`

interface OptionsProps {
  isOpen: boolean
  width?: string
  hasError: boolean
}

const Options = styled.div<OptionsProps>`
  display: ${(props) => (props.isOpen ? "block" : "none")};

  position: absolute;
  z-index: 4000;
  top: 46px;
  left: 0;
  right: 0;

  margin: 0;
  padding: 0;
  box-sizing: border-box;

  box-shadow: ${(props) => props.theme.shadow.primary};

  list-style: none;
  background: white;
  border-radius: 0 0 4px 4px;

  ${(props) =>
    props.isOpen &&
    css`
      border: 2px solid ${props.theme.colors.primary};

      border-top-right-radius: 0;
      border-top-left-radius: 0;
      border-top: none;
    `}

  ${(props) =>
    props.hasError &&
    css`
      border: 2px solid ${props.theme.colors.error40};
      border-top: none;
    `}
`

const OptionLabel = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 12px;
  box-sizing: border-box;
  gap: 0.5rem;

  height: 48px;
  color: ${(props) => props.theme.colors.gray30};

  &:hover {
    background: ${(props) => props.theme.colors.primary95};
  }
`

const OptionIconWrapper = styled.div`
  line-height: 0;
`

const Option = styled.li`
  ${OptionLabel}:last-of-type {
    border-radius: 0 0 8px 8px;
  }
`

const Error = styled.div`
  color: ${(props) => props.theme.colors.error40};
  font-size: 12px;
  margin-top: 4px;
  margin-left: 4px;
`

const Warning = styled.div`
  color: ${(props) => props.theme.colors.warning40};
  font-size: 12px;
  margin-top: 4px;
  margin-left: 4px;

  position: absolute;
`

export interface SearchableSelectProps<
  ValueType extends AllowedOptionValueType,
  ExtraDataType = any,
> {
  value: ValueType | null
  onChange: (newValue: ValueType | null, extraData?: ExtraDataType) => void
  search?: string
  onSearchChanged: (newValue: string) => void
  options: SelectOption<ValueType, ExtraDataType>[]
  clearableValue?: ValueType

  width?: string
  placeholder?: string
  placeholderColor?: string

  error?: string | null
  warning?: string | null
}

export const SearchableSelect = <
  ValueType extends AllowedOptionValueType,
  ExtraDataType,
>({
  value,
  onChange,
  search: _search,
  onSearchChanged,
  options,
  width,
  placeholder: _placeholder,
  placeholderColor: _placeholderColor,
  clearableValue,
  error,
  warning,
}: SearchableSelectProps<ValueType, ExtraDataType>) => {
  const ref = useRef(null)
  const theme = useTheme()
  const [isOpen, setIsOpen] = useState(false)
  const translations = useTranslate({
    defaultPlaceholder: "common.select-placeholder",
    clear: "common.clear",
  })

  const placeholder = _placeholder || translations.defaultPlaceholder
  const selectedOption = options.find((option) => value === option.value)
  const placeholderColor = _placeholderColor || theme.colors.gray50
  const isPlaceholderDisplayed = value === clearableValue

  const [search, setSearch] = useState(
    _search ?? selectedOption?.label.toString() ?? "",
  )

  useEffect(() => {
    if (_search !== undefined) {
      setSearch(_search)
    }
  }, [_search])

  useEffect(() => {
    if (!selectedOption) {
      return
    }

    setSearch(selectedOption.label.toString())
  }, [selectedOption])

  const handleSearchChanged = useCallback(
    (newSearch: string) => {
      setSearch(newSearch)
      onSearchChanged(newSearch)

      if (newSearch !== selectedOption?.label.toString()) {
        onChange(null)
      }
    },
    [onChange, onSearchChanged, selectedOption],
  )

  useClickOutside(ref, () => {
    setIsOpen(false)
  })

  return (
    <Layout ref={ref} isOpen={isOpen} width={width}>
      <Display
        isOpen={isOpen}
        width={width}
        hasError={!!error}
        isPlaceholderDisplayed={isPlaceholderDisplayed}
        placeholderColor={placeholderColor}
      >
        <DisplayInput
          value={search}
          onChange={handleSearchChanged}
          placeholder={placeholder}
          placeholderColor={placeholderColor}
          hasError={!!error}
          onFocus={() => setIsOpen(true)}
          border="none"
        />
        <ChevronWrapper>
          <Icon
            icon={value ? "check" : "search"}
            color={value ? theme.colors.success50 : theme.colors.gray50}
          />
        </ChevronWrapper>
      </Display>
      {error && <Error>{error}</Error>}
      {warning && <Warning>{warning}</Warning>}
      <Options isOpen={isOpen} width={width} hasError={!!error}>
        {options
          .filter((option) => option.value !== value)
          .map((option) => (
            <Option
              key={option.value}
              onClick={(e) => {
                e.stopPropagation()
                onChange(option.value, option.extra)
                setIsOpen(false)
              }}
            >
              <OptionLabel>
                {option.icon && (
                  <OptionIconWrapper>{option.icon}</OptionIconWrapper>
                )}
                {option.label}
              </OptionLabel>
            </Option>
          ))}
      </Options>
    </Layout>
  )
}

export default SearchableSelect
